import { useMemo } from "react";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import {
  NJBadge,
  NJButton,
  NJHeading,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useParams } from "react-router-dom";
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { BackButton } from "@/components/BackButton";
import { DataTable } from "@/components/DataTable";
import { MetaTagList } from "@/components/store/MetaTagList";
import type { components } from "@/api/generated-types";
import { SkillCompatibilityTags } from "./SkillCompatibilityTags";
import { downloadSkill, useSkill } from "./useSkills";

type SkillVersion = components["schemas"]["SkillVersionRead"];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Skill detail — description, compatibility, categories, version history and download. */
export function SkillDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: skill, isLoading, isError } = useSkill(slug);

  const versionColumns = useMemo<ColDef<SkillVersion>[]>(
    () => [
      {
        headerName: "Version",
        field: "version",
        flex: 1,
        cellRenderer: (params: ICellRendererParams<SkillVersion>) => (
          <span className="inline-flex items-center gap-2">
            v{params.value}
            {params.data?.isCurrent && (
              <NJBadge variant="success">Current</NJBadge>
            )}
          </span>
        ),
      },
      { headerName: "File", field: "fileName", flex: 2 },
      {
        headerName: "Size",
        field: "sizeBytes",
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<SkillVersion, number>) =>
          formatBytes(params.value ?? 0),
      },
      {
        headerName: "Uploaded",
        field: "uploadedAt",
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<SkillVersion, string>) =>
          params.value
            ? new Date(params.value).toLocaleDateString("en-GB")
            : "",
      },
      {
        headerName: "",
        flex: 1,
        cellRenderer: (params: ICellRendererParams<SkillVersion>) => (
          <NJIconButton
            icon="download"
            variant="tertiary"
            label={`Download v${params.data?.version ?? ""}`}
            onClick={() =>
              slug &&
              params.data &&
              void downloadSkill(slug, params.data.version)
            }
          />
        ),
      },
    ],
    [slug],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <NJSpinner aria-label="Loading the skill" />
      </div>
    );
  }

  if (isError || !skill) {
    return <NJInlineMessage variant="error">Skill not found.</NJInlineMessage>;
  }

  const versions = skill.versions ?? [];

  return (
    <div className="flex w-full flex-col gap-5">
      <BackButton to="/skills" label="Back to Skill Store" />
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-nj-neutral-subtle-default pb-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-nj-brand-secondary-default text-nj-brand-solid-default">
            <NJIcon name="inventory_2" />
          </span>
          <div className="flex flex-col">
            <NJHeading as="h1" scale="lg">
              {skill.name}
            </NJHeading>
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              {skill.sourceType === "GITHUB"
                ? "GitHub repository"
                : `ZIP package • ${
                    skill.currentVersion
                      ? `v${skill.currentVersion}`
                      : "no version yet"
                  }`}
            </NJText>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {skill.sourceType === "ZIP" ? (
            <NJButton
              label="Download"
              icon="download"
              disabled={!skill.currentVersion}
              onClick={() => void downloadSkill(skill.slug)}
            />
          ) : (
            skill.repositoryUrl && (
              <a
                href={skill.repositoryUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-nj-brand-solid-default no-underline hover:underline"
              >
                <NJIcon name="open_in_new" />
                <NJText as="span" scale="sm">
                  Open repository
                </NJText>
              </a>
            )
          )}
        </div>
      </header>

      <article className="mx-auto flex w-full max-w-[1240px] flex-col gap-4 border border-nj-neutral-subtle-default bg-nj-neutral-primary-default p-6 shadow-sm">
        <div className="flex flex-col gap-1.5">
          <NJText
            as="span"
            scale="xs"
            className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
          >
            Compatible with
          </NJText>
          <SkillCompatibilityTags items={skill.compatibleHarnesses ?? []} />
        </div>
        <MetaTagList label="Categories" items={skill.includedSkills ?? []} />

        {skill.fullDescription ? (
          <MarkdownMessage content={skill.fullDescription} />
        ) : (
          <NJText
            as="p"
            scale="sm"
            className="text-nj-neutral-secondary-default"
          >
            {skill.description || "No description provided."}
          </NJText>
        )}

        <NJText
          as="span"
          scale="xs"
          className="text-nj-neutral-secondary-default"
        >
          Creator <strong>{skill.createdByName || "-"}</strong>
        </NJText>
      </article>

      {versions.length > 0 && (
        <section className="mx-auto flex w-full max-w-[1240px] flex-col gap-2">
          <NJHeading as="h2" scale="sm">
            Versions
          </NJHeading>
          <DataTable<SkillVersion>
            rowData={versions}
            columnDefs={versionColumns}
          />
        </section>
      )}
    </div>
  );
}
